import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    MenuItem,
    Typography,
    Box,
    Card,
    CardContent,
    LinearProgress,
    InputAdornment,
    IconButton,
    Fade,
    Zoom,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
    Business,
    Email,
    Phone,
    LocationOn,
    CloudUpload,
    CheckCircle,
    ArrowBack,
    ArrowForward,
} from "@material-ui/icons";
import { withRouter } from "react-router-dom";
import logo from "../assets/images/logo.png";
import { API } from "../config";

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
                "radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
            animation: "$float 20s ease-in-out infinite",
        },
    },
    "@keyframes float": {
        "0%, 100%": { transform: "translate(0, 0)" },
        "50%": { transform: "translate(20px, 20px)" },
    },
    container: {
        position: "relative",
        zIndex: 1,
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
    },
    logoContainer: {
        marginBottom: theme.spacing(3),
        animation: "$fadeInDown 0.8s ease-out",
    },
    "@keyframes fadeInDown": {
        from: {
            opacity: 0,
            transform: "translateY(-30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },
    logo: {
        maxHeight: 80,
        filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))",
    },
    card: {
        maxWidth: 800,
        width: "100%",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(20px)",
        borderRadius: theme.spacing(3),
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        padding: theme.spacing(4),
        animation: "$fadeInUp 0.8s ease-out",
    },
    "@keyframes fadeInUp": {
        from: {
            opacity: 0,
            transform: "translateY(30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },
    title: {
        fontWeight: 700,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        marginBottom: theme.spacing(1),
        textAlign: "center",
    },
    subtitle: {
        textAlign: "center",
        color: "#6b7280",
        marginBottom: theme.spacing(4),
    },
    stepper: {
        background: "transparent",
        padding: theme.spacing(3, 0),
    },
    stepLabel: {
        "& .MuiStepIcon-root.MuiStepIcon-active": {
            color: "#667eea",
        },
        "& .MuiStepIcon-root.MuiStepIcon-completed": {
            color: "#10b981",
        },
    },
    formSection: {
        marginTop: theme.spacing(3),
    },
    textField: {
        marginBottom: theme.spacing(2),
        "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
                borderColor: "#667eea",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#667eea",
            },
        },
    },
    buttonGroup: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: theme.spacing(4),
    },
    button: {
        padding: theme.spacing(1.5, 4),
        borderRadius: theme.spacing(3),
        fontWeight: 600,
        textTransform: "none",
        fontSize: "1rem",
    },
    backButton: {
        color: "#6b7280",
        "&:hover": {
            background: "#f3f4f6",
        },
    },
    nextButton: {
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        "&:hover": {
            background: "linear-gradient(135deg, #5568d3 0%, #6a4190 100%)",
            transform: "translateY(-2px)",
            boxShadow: "0 10px 20px rgba(102, 126, 234, 0.3)",
        },
        transition: "all 0.3s ease",
    },
    uploadButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        border: "2px dashed #d1d5db",
        borderRadius: theme.spacing(2),
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
            borderColor: "#667eea",
            background: "#f9fafb",
        },
    },
    uploadedFile: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        background: "#f0fdf4",
        borderRadius: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    successContainer: {
        textAlign: "center",
        padding: theme.spacing(4),
    },
    successIcon: {
        fontSize: 80,
        color: "#10b981",
        animation: "$scaleIn 0.5s ease-out",
    },
    "@keyframes scaleIn": {
        from: {
            transform: "scale(0)",
        },
        to: {
            transform: "scale(1)",
        },
    },
}));

const steps = ["Company Info", "Contact Details", "Location", "Additional Info"];

const CompanySignup = ({ history }) => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        // Step 1: Company Info
        companyName: "",
        categoryId: "",
        description: "",

        // Step 2: Contact Details
        email: "",
        phoneNumber: "",
        officeNumber: "",
        faxNumber: "",

        // Step 3: Location
        city: "",
        subCity: "",
        woreda: "",
        kebele: "",
        street: "",
        poBox: "",

        // Step 4: Additional
        website: "",
        logo: null,
        license: null,
    });

    useEffect(() => {
        // Fetch main/parent categories from the database
        fetch(`${API}/view-main-catagories`)
            .then((res) => res.json())
            .then((data) => {
                if (data.result) {
                    setCategories(data.result);
                }
            })
            .catch((err) => console.error("Error fetching categories:", err));
    }, []);

    const handleChange = (field) => (event) => {
        setFormData({ ...formData, [field]: event.target.value });
        setError("");
    };

    const handleFileChange = (field) => (event) => {
        setFormData({ ...formData, [field]: event.target.files[0] });
    };

    const validateStep = () => {
        switch (activeStep) {
            case 0:
                if (!formData.companyName || !formData.categoryId || !formData.description) {
                    setError("Please fill in all required fields");
                    return false;
                }
                break;
            case 1:
                if (!formData.email || !formData.phoneNumber) {
                    setError("Please fill in all required fields");
                    return false;
                }
                // Email validation
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) {
                    setError("Please enter a valid email address");
                    return false;
                }
                break;
            case 2:
                if (!formData.city || !formData.subCity || !formData.woreda || !formData.street) {
                    setError("Please fill in all required fields");
                    return false;
                }
                break;
            case 3:
                if (!formData.logo || !formData.license) {
                    setError("Please upload both company logo and business license");
                    return false;
                }
                break;
            default:
                break;
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            if (activeStep === steps.length - 1) {
                handleSubmit();
            } else {
                setActiveStep((prevStep) => prevStep + 1);
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
        setError("");
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError("");

        const data = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key]) {
                data.append(key, formData[key]);
            }
        });

        try {
            const response = await fetch(`${API}/company-signup`, {
                method: "POST",
                body: data,
            });

            const result = await response.json();

            if (result.error || result.err) {
                setError(result.error || result.err);
                setLoading(false);
            } else {
                setSuccess(true);
                setLoading(false);
                setTimeout(() => {
                    history.push("/");
                }, 3000);
            }
        } catch (err) {
            setError("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Fade in timeout={500}>
                        <Box>
                            <TextField
                                fullWidth
                                label="Company Name"
                                variant="outlined"
                                required
                                value={formData.companyName}
                                onChange={handleChange("companyName")}
                                className={classes.textField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Business color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                select
                                label="Category"
                                variant="outlined"
                                required
                                value={formData.categoryId}
                                onChange={handleChange("categoryId")}
                                className={classes.textField}
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category.Id} value={category.Id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                label="Company Description"
                                variant="outlined"
                                required
                                multiline
                                rows={4}
                                value={formData.description}
                                onChange={handleChange("description")}
                                className={classes.textField}
                                placeholder="Tell us about your company..."
                            />
                        </Box>
                    </Fade>
                );

            case 1:
                return (
                    <Fade in timeout={500}>
                        <Box>
                            <TextField
                                fullWidth
                                label="Email Address"
                                variant="outlined"
                                required
                                type="email"
                                value={formData.email}
                                onChange={handleChange("email")}
                                className={classes.textField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Phone Number"
                                variant="outlined"
                                required
                                value={formData.phoneNumber}
                                onChange={handleChange("phoneNumber")}
                                className={classes.textField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Office Number (Optional)"
                                variant="outlined"
                                value={formData.officeNumber}
                                onChange={handleChange("officeNumber")}
                                className={classes.textField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Fax Number (Optional)"
                                variant="outlined"
                                value={formData.faxNumber}
                                onChange={handleChange("faxNumber")}
                                className={classes.textField}
                            />
                        </Box>
                    </Fade>
                );

            case 2:
                return (
                    <Fade in timeout={500}>
                        <Box>
                            <TextField
                                fullWidth
                                label="City"
                                variant="outlined"
                                required
                                value={formData.city}
                                onChange={handleChange("city")}
                                className={classes.textField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationOn color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Sub City"
                                variant="outlined"
                                required
                                value={formData.subCity}
                                onChange={handleChange("subCity")}
                                className={classes.textField}
                            />
                            <TextField
                                fullWidth
                                label="Woreda"
                                variant="outlined"
                                required
                                value={formData.woreda}
                                onChange={handleChange("woreda")}
                                className={classes.textField}
                            />
                            <TextField
                                fullWidth
                                label="Kebele"
                                variant="outlined"
                                value={formData.kebele}
                                onChange={handleChange("kebele")}
                                className={classes.textField}
                            />
                            <TextField
                                fullWidth
                                label="Street Address"
                                variant="outlined"
                                required
                                value={formData.street}
                                onChange={handleChange("street")}
                                className={classes.textField}
                            />
                            <TextField
                                fullWidth
                                label="P.O. Box (Optional)"
                                variant="outlined"
                                value={formData.poBox}
                                onChange={handleChange("poBox")}
                                className={classes.textField}
                            />
                        </Box>
                    </Fade>
                );

            case 3:
                return (
                    <Fade in timeout={500}>
                        <Box>
                            <TextField
                                fullWidth
                                label="Website (Optional)"
                                variant="outlined"
                                value={formData.website}
                                onChange={handleChange("website")}
                                className={classes.textField}
                                placeholder="https://www.example.com"
                            />

                            <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                                Company Logo *
                            </Typography>
                            <Box className={classes.uploadButton}>
                                <input
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    id="logo-upload"
                                    type="file"
                                    onChange={handleFileChange("logo")}
                                />
                                <label htmlFor="logo-upload" style={{ cursor: "pointer", display: "block" }}>
                                    <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                                        <CloudUpload style={{ fontSize: 40, color: "#9ca3af", marginBottom: 8 }} />
                                        <Typography variant="body2" color="textSecondary">
                                            Click to upload company logo
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            PNG, JPG up to 5MB
                                        </Typography>
                                    </Box>
                                </label>
                            </Box>
                            {formData.logo && (
                                <Box className={classes.uploadedFile}>
                                    <CheckCircle style={{ color: "#10b981" }} />
                                    <Typography variant="body2">{formData.logo.name}</Typography>
                                </Box>
                            )}

                            <Typography variant="subtitle2" gutterBottom style={{ marginTop: 24 }}>
                                Business License *
                            </Typography>
                            <Box className={classes.uploadButton}>
                                <input
                                    accept="image/*,application/pdf"
                                    style={{ display: "none" }}
                                    id="license-upload"
                                    type="file"
                                    onChange={handleFileChange("license")}
                                />
                                <label htmlFor="license-upload" style={{ cursor: "pointer", display: "block" }}>
                                    <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                                        <CloudUpload style={{ fontSize: 40, color: "#9ca3af", marginBottom: 8 }} />
                                        <Typography variant="body2" color="textSecondary">
                                            Click to upload business license
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            PNG, JPG, PDF up to 10MB
                                        </Typography>
                                    </Box>
                                </label>
                            </Box>
                            {formData.license && (
                                <Box className={classes.uploadedFile}>
                                    <CheckCircle style={{ color: "#10b981" }} />
                                    <Typography variant="body2">{formData.license.name}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                );

            default:
                return null;
        }
    };

    if (success) {
        return (
            <div className={classes.root}>
                <div className={classes.container}>
                    <Card className={classes.card}>
                        <CardContent>
                            <Zoom in timeout={500}>
                                <Box className={classes.successContainer}>
                                    <CheckCircle className={classes.successIcon} />
                                    <Typography variant="h4" style={{ fontWeight: 700, marginTop: 24, marginBottom: 16 }}>
                                        Registration Successful!
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary">
                                        Thank you for registering your company. Our team will review your application and get back to you
                                        soon.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                                        Redirecting to homepage...
                                    </Typography>
                                </Box>
                            </Zoom>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <div className={classes.logoContainer}>
                    <img src={logo} className={classes.logo} alt="Logo" />
                </div>

                <Card className={classes.card}>
                    <CardContent>
                        <Typography variant="h4" className={classes.title}>
                            Register Your Company
                        </Typography>
                        <Typography variant="body2" className={classes.subtitle}>
                            Join InfoEthiopia and grow your business
                        </Typography>

                        <Stepper activeStep={activeStep} className={classes.stepper}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel className={classes.stepLabel}>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {loading && <LinearProgress style={{ marginBottom: 16 }} />}

                        {error && (
                            <Alert severity="error" style={{ marginBottom: 16 }}>
                                {error}
                            </Alert>
                        )}

                        <Box className={classes.formSection}>{renderStepContent()}</Box>

                        <Box className={classes.buttonGroup}>
                            <Button
                                disabled={activeStep === 0 || loading}
                                onClick={handleBack}
                                className={`${classes.button} ${classes.backButton}`}
                                startIcon={<ArrowBack />}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={loading}
                                className={`${classes.button} ${classes.nextButton}`}
                                endIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
                            >
                                {activeStep === steps.length - 1 ? "Submit" : "Next"}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default withRouter(CompanySignup);
